<template>
  <el-drawer
    v-model="drawerVisible"
    class="add-device-drawer"
    :modal="false"
    :show-close="false"
    size="480px"
    title="添加设备"
  >
    <template #header>
      <div class="add-device__header">
        <i class="icon-arrow-left-s-line t-iconfont" @click="closeDrawer"></i>
        <h2 class="add-device__title">添加设备</h2>
      </div>
    </template>

    <div class="add-device__body">
      <div class="add-device__tips">
        <i class="add-device__tips-icon icon-error-warning-fill t-iconfont"></i>
        <p class="add-device__tips-text">
          提交后将自动关联到 Dudutalk 的“{{
            deviceName
          }}“分组。未选择员工的设备后续也可随时绑定员工。
        </p>
      </div>

      <div class="add-device__rows">
        <div v-for="(item, index) in deviceRows" :key="item.id" class="add-device__row">
          <div class="add-device__item">
            <span class="add-device__row-index">{{ index + 1 }}</span>
            <el-input
              v-model.trim="item.deviceNo"
              class="add-device__row-input"
              :class="{ 'add-device__row-input--error': item.status === 0 }"
              clearable
              placeholder="请输入或粘贴设备号"
              @blur="handleDeviceBlur(item)"
            />
            <UserSelect
              v-model:value="item.employeeId"
              class="add-device__row-input add-device__row-user-select"
              placeholder="请选择员工（选填）"
              @select-user="(user) => handleSelectEmployee(item, user)"
            />
            <button
              aria-label="删除设备"
              class="add-device__delete"
              type="button"
              @click="removeDeviceRow(index)"
            >
              <i class="icon-delete-bin-line t-iconfont"></i>
            </button>
          </div>
          <span v-if="item.msg" class="msg">{{ item.msg }}</span>
        </div>
      </div>

      <button class="add-device__add-row" type="button" @click="addDeviceRow">
        <i class="icon-add-line t-iconfont"></i>
        <span>添加</span>
      </button>
    </div>

    <template #footer>
      <div class="add-device__footer">
        <el-button @click="closeDrawer">取消</el-button>
        <el-button :loading="isSubmitting" type="primary" @click="handleConfirm"
          >确认提交</el-button
        >
      </div>
    </template>
  </el-drawer>
</template>

<script setup>
import { computed, inject, ref, watch } from 'vue'
import { addBadge, checkAddBadge } from '@/api/ai-setting'
import AppMessage from '@/components/message/index.js'
import UserSelect from '@/components/user-select.vue'
import { getDeviceType, getDeviceName } from './add-badge-submit.js'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  deviceTabs: {
    type: Array,
    default: () => []
  },
  activeTab: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:visible'])
const refreshDeviceList = inject('refreshDeviceList')

let rowId = 0

const drawerVisible = computed({
  get() {
    return props.visible
  },
  set(value) {
    emit('update:visible', value)
  }
})

const createDeviceRow = () => ({
  id: ++rowId,
  deviceNo: '',
  employeeId: '',
  employeeName: '',
  msg: '', // 暂存的提示消息
  status: '' // 1为正常，0为已经绑定
})

const createDefaultRows = () => [createDeviceRow(), createDeviceRow(), createDeviceRow()]

const deviceRows = ref(createDefaultRows())

const deviceName = computed(() => getDeviceName(props.activeTab))
const deviceType = computed(() => getDeviceType(props.activeTab))

const resetRows = () => {
  deviceRows.value = createDefaultRows()
}

const closeDrawer = () => {
  drawerVisible.value = false
}

const addDeviceRow = () => {
  deviceRows.value.push(createDeviceRow())
}

const removeDeviceRow = (index) => {
  deviceRows.value.splice(index, 1)
}

const handleSelectEmployee = (row, user) => {
  row.employeeName = user?.name || ''
}

const handleDeviceBlur = (row) => {
  if (!row.deviceNo) return
  // 如果输入了值，立即重置状态
  row.status = ''
  checkAddBadge({ deviceNo: row.deviceNo, type: deviceType.value })
    .then((res) => {
      row.status = res.result.status
      if (!row.status) {
        row.msg = res.result.msg
        AppMessage.error(res.result.msg)
      }
    })
    .catch(() => {
      AppMessage.error('硬件配置同步失败，请稍后重试')
    })
}
const isSubmitting = ref(false)

const handleConfirm = async () => {
  const filledRows = deviceRows.value.filter((row) => row.deviceNo)
  if (!filledRows.length) {
    AppMessage.error('请至少填写一个设备号')
    return
  }
  // 只要有一个状态不正常就禁止提交（只有等于 1 才是正常）
  if (filledRows.some((row) => row.status !== 1)) return

  isSubmitting.value = true
  try {
    const badgeList = filledRows.map(({ deviceNo, employeeId }) => {
      return {
        deviceNo,
        userId: employeeId
      }
    })
    await addBadge({ badgeList, type: deviceType.value })
    AppMessage.success('添加成功')
    closeDrawer()
    await refreshDeviceList?.()
  } catch (error) {
    console.error('硬件配置同步失败，请稍后重试:', error)
    AppMessage.error('硬件配置同步失败，请稍后重试')
  } finally {
    isSubmitting.value = false
  }
}

watch(
  () => props.visible,
  (visible) => {
    if (visible) resetRows()
  }
)
</script>

<style lang="scss" scoped>
.add-device {
  &__header {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    .icon-arrow-left-s-line {
      font-size: 24px;
      color: #646566;
      cursor: pointer;
    }
  }

  &__title {
    margin: 0;
    font-family: PingFang SC;
    font-size: 16px;
    line-height: 22px;
    font-weight: 600;
    color: #323233;
  }

  &__body {
    padding: 16px;
  }

  &__tips {
    display: flex;
    align-items: center;
    min-height: 64px;
    padding: 12px 16px;
    border: 1px solid #c1e8ff;
    border-radius: 12px;
    background: #e8f7ff;
    box-sizing: border-box;
  }

  &__tips-icon {
    flex: 0 0 auto;
    margin-right: 8px;
    font-size: 20px;
    color: #248df5;
  }

  &__tips-text {
    flex: 1;
    margin: 0;
    font-family: PingFang SC;
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    color: #323233;
  }

  &__rows {
    margin-top: 12px;
  }

  &__row {
    padding: 8px 12px;
    margin-bottom: 12px;
    border: 1px solid #e5e6eb;
    border-radius: 12px;
    box-sizing: border-box;
    .msg {
      font-size: 12px;
      line-height: 16px;
      color: #f54e4e;
      margin-left: 30px;
    }
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__row-index {
    flex: 0 0 18px;
    font-family: PingFang SC;
    font-size: 14px;
    line-height: 20px;
    color: #323233;
    text-align: center;
  }

  &__row-input {
    flex: 1;
    min-width: 0;

    :deep(.el-input__wrapper) {
      height: 32px;
      border-radius: 8px;
      padding: 0 12px;
    }

    :deep(.el-input__inner) {
      font-family: PingFang SC;
      font-size: 14px;
      color: #323233;

      &::placeholder {
        color: #c8c9cc;
      }
    }
  }

  &__row-input--error {
    :deep(.el-input__wrapper) {
      box-shadow: 0 0 0 1px #f54e4e inset;
    }
  }

  &__delete {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 16px;
    width: 16px;
    height: 16px;
    padding: 0;
    border: none;
    background: transparent;
    color: #969799;
    cursor: pointer;

    &:hover {
      color: #f54e4e;
    }
  }

  &__add-row {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 48px;
    padding: 8px 12px;
    border: 1px dashed #e5e6eb;
    border-radius: 12px;
    background: transparent;
    font-family: PingFang SC;
    font-size: 14px;
    line-height: 20px;
    color: #9e9e9e;
    cursor: pointer;
    box-sizing: border-box;

    &:hover {
      background: #f5e8ff;
      color: #7a52f4;
    }

    i {
      margin-right: 4px;
    }
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
